// Leaf predicates over a post's status. Kept apart from anchor.ts so
// distribute.ts and elect.ts can share them without a cycle (anchor.ts
// imports pubKey from distribute.ts).

import { COMMITTED_PUBLICATION_STATUSES } from "../../../utils/PublicationHelper";

/** A post that is live or in flight on the platform. Nothing in the dialog
 *  edits, moves or deletes it. */
export const IMMUTABLE_STATUSES = new Set([
  "published",
  "publishing",
  "verifying",
]);

export function isImmutablePub(p: { status: string }): boolean {
  return IMMUTABLE_STATUSES.has(p.status);
}

/**
 * A post the publisher tried and gave up on. Not live, not booked: it is
 * editable like a draft — a Save re-books it (status → scheduled, error
 * cleared) — and never a plan: the folder's committed set
 * (COMMITTED_PUBLICATION_STATUSES), the engine's anchors (isSavedPub) and
 * the read face all leave it out, and the queue row wears it as its one
 * alert ("Failed") until the user touches it (§4).
 */
export function isFailedPub(p: { status: string }): boolean {
  return p.status === "failed";
}

/**
 * A post whose attempt has an outcome — live, or failed. Neither is
 * schedulable: the row's date, the per-video range pill and the batch's
 * "Starting …" bound all skip both, so history never dates a row (§13.7)
 * and a dead post never pulls the plan into the past.
 */
export function isSettledPub(p: { status: string }): boolean {
  return p.status === "published" || isFailedPub(p);
}

/**
 * The one rule for whether a persisted post can still be scheduled, moved,
 * re-booked or deleted — shared by the Calendar post menu, the edit-mode
 * shorts picker and the schedule cards. Scheduled and failed are editable;
 * publishing / verifying are in flight and published is history, none of
 * which the dialog touches (IMMUTABLE_STATUSES). A draft is not persisted.
 */
export function isEditablePersistedPub(p: { status: string }): boolean {
  return p.status === "scheduled" || isFailedPub(p);
}

/**
 * A post that is part of a folder's plan — booked, live or in flight
 * (COMMITTED_PUBLICATION_STATUSES). A short holding one is COMMITTED: its
 * plan is what it is and never grows on its own — the election (§6)
 * materializes no automatic draft on it on entry or on ADD_VIDEO; a
 * destination is added to it explicitly, with the short's "+" (a provider
 * pin) or by checking a provider the plan did not have in the header.
 * Without this, a post the user removed and saved
 * came back on the next entry as "coverage". A failed post is not a plan
 * (§4), so a short whose only posts failed still fills the pool on entry.
 */
export function isCommittedPub(p: { status: string }): boolean {
  return (COMMITTED_PUBLICATION_STATUSES as ReadonlySet<string>).has(p.status);
}
